import { Divider, Grid, makeStyles } from '@material-ui/core';
import { DeleteSweep } from '@material-ui/icons';
import Button from 'components/Button';
import { SearchBar } from 'components/SearchBar';
import { Query } from 'hooks/useTableFilters';
import i18n from 'i18n';
import React from 'react';
import {
  ConditionOperatorsEnum,
  Filter,
  getOperatorByValue,
  SelectedFilter,
  generateQueryFilters,
} from 'utils/filters';
import FilterableMenu from './FilterableMenu';
import SelectedFilters from './SelectedFilters';
import styles from './styles';
import FilteredItemsCounter, {
  FilteredItemsCounterProps,
} from './FilteredItemsCounter';

interface Props {
  filteredItemsCounterProps?: FilteredItemsCounterProps;
  filterOptions: Filter[];
  isLoading: boolean;
  searchValue: string;
  query: Query;
  onApplyFilters: () => void;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  setQuery: React.Dispatch<React.SetStateAction<Query>>;
}

const SearchFilterBar = ({
  filteredItemsCounterProps,
  filterOptions,
  isLoading,
  searchValue,
  query,
  onApplyFilters,
  setSearchValue,
  setQuery,
}: Props) => {
  const classes = makeStyles(styles)();

  const getInitialSelectedFilters = React.useCallback(() => {
    if (!query.filters || typeof query.filters !== 'object') return [];

    return Object.entries(query.filters).reduce((acc, [key, value]) => {
      let field = key;
      let operator = '';

      if (key.includes('__')) {
        [field, operator] = key.split('__');
        operator = `__${operator}`;
      } else if (key.endsWith('!')) {
        field = key.slice(0, -1);
        operator = '!';
      }

      const filter = filterOptions.find(item => item.field === field);
      if (filter) {
        acc.push({
          ...filter,
          operator:
            getOperatorByValue(operator) ||
            filter.defaultOperator ||
            ConditionOperatorsEnum.IS,
          value: value as string,
        });
      }

      return acc;
    }, [] as SelectedFilter[]);
  }, [filterOptions, query.filters]);

  const [selectedFilters, setSelectedFilters] = React.useState<
    SelectedFilter[]
  >(getInitialSelectedFilters());

  React.useEffect(() => {
    setSelectedFilters(getInitialSelectedFilters());
  }, [getInitialSelectedFilters]);

  const addFilter = (filter: Filter) => {
    setSelectedFilters(prev => [
      ...prev,
      {
        ...filter,
        operator: filter.defaultOperator || ConditionOperatorsEnum.IS,
      },
    ]);
  };

  const deleteFilter = (filter: SelectedFilter) => {
    setSelectedFilters(prev =>
      prev.filter(item => item.field !== filter.field)
    );
  };

  const toggleFilter = (filter: Filter) => {
    if (selectedFilters.some(item => item.field === filter.field)) {
      deleteFilter(filter as SelectedFilter);
    } else {
      addFilter(filter);
    }
  };

  const clearFilters = () => {
    setSelectedFilters([]);
  };

  const updateFilter = (filter: SelectedFilter) => {
    setSelectedFilters(prev =>
      prev.map(item =>
        item.field === filter.field ? { ...item, ...filter } : item
      )
    );
  };

  const applyFilters = (newFilters?: SelectedFilter[]) => {
    const filters = generateQueryFilters(newFilters ?? selectedFilters);

    setQuery(prev => ({
      ...prev,
      offset: 0,
      search: searchValue.trim().length >= 3 ? searchValue : '',
      filters,
    }));

    onApplyFilters();
  };

  return (
    <Grid container className={classes.container}>
      <Grid container alignItems="center">
        <Grid item xs={4} className={classes.searchBar}>
          <SearchBar
            id="search-bar"
            isLoading={isLoading}
            handleSearch={() => applyFilters()}
            setSearchValue={setSearchValue}
          />
        </Grid>

        <Divider orientation="vertical" variant="middle" />

        <Grid container item xs={6}>
          <Grid className={classes.buttonsContainer}>
            <FilterableMenu
              filters={filterOptions}
              selectedFilters={selectedFilters}
              onCheckFilter={filter => toggleFilter(filter)}
            />
            <Button
              variant="outlined"
              onClick={() => applyFilters()}
              disabled={isLoading}
            >
              {i18n.t('SEARCH_ALL_KEY')}
            </Button>

            {!!selectedFilters.length && (
              <Grid className={classes.cleanContainer}>
                <Divider orientation="vertical" variant="middle" />
                <Button
                  className={classes.cleanButton}
                  onClick={() => {
                    clearFilters();
                    applyFilters([]);
                  }}
                  startIcon={<DeleteSweep />}
                >
                  {i18n.t('CLEAR_FORM_KEY')}
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
      <SelectedFilters
        selectedFilters={selectedFilters}
        onDeleteFilter={deleteFilter}
        onUpdateFilter={updateFilter}
        onEnterPress={() => applyFilters()}
      />
      {!!selectedFilters.length && filteredItemsCounterProps && (
        <FilteredItemsCounter {...filteredItemsCounterProps} />
      )}
    </Grid>
  );
};

export default SearchFilterBar;
